import React from "react";
import { usePageView } from "../../utils/track";
import { useParams } from "react-router-dom";
import { Calendar, MapPin, Target } from "lucide-react";
import { Breadcrumb } from "../../layouts/PublicLayout";
import { Badge, EmptyState, InfoRow, Loading, SectionCard, statusTone } from "../../components/ui";
import CompetitionSidebar from "../../components/CompetitionSidebar";
import ResultsBlock from "../../components/ResultsBlock";
import { useApi } from "../../api/client";
import { Competition, CompetitionEvent, ResultRow } from "../../data/types";
import { formatScore } from "../../utils/score";

export default function EventResults() {
  usePageView("day-results");
  const { id, eventId } = useParams();
  const { data: c, loading: cLoading } = useApi<(Competition & { eventList?: CompetitionEvent[] }) | null>(
    id ? `/competitions/${id}` : null,
    null
  );
  const { data: e, loading: eLoading } = useApi<CompetitionEvent | null>(eventId ? `/events/${eventId}` : null, null);
  const { data: eventResults } = useApi<ResultRow[]>(eventId ? `/events/${eventId}/results` : null, []);

  if (cLoading || eLoading) return <div className="mx-auto max-w-[1720px] px-4 py-4 sm:px-5"><div className="card"><Loading /></div></div>;
  if (!c || !e) {
    return (
      <div className="mx-auto max-w-[1720px] px-4 py-4 sm:px-5">
        <div className="card">
          <EmptyState title="Event not found" message="It may have been removed, or no shoot has been imported yet." />
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-[1720px] px-4 py-4 sm:px-5">
      <Breadcrumb
        items={[
          { label: "Home", to: "/" },
          { label: "Events", to: "/competitions" },
          { label: c.name, to: `/competitions/${c.id}` },
          { label: "Event Results" },
        ]}
      />

      <div className="mt-3 flex flex-col gap-4 lg:flex-row">
        <CompetitionSidebar competition={c} active="events" activeEventId={e.id} events={c.eventList} />

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <div className="text-[13px] font-bold uppercase tracking-widest text-primary">Day {e.n}</div>
              <h1 className="mt-1 text-2xl font-extrabold tracking-tight text-ink">{e.name}</h1>
              <div className="mt-2 flex flex-wrap items-center gap-x-5 gap-y-1 text-sm text-muted">
                <span className="flex items-center gap-1.5"><Calendar size={15} /> {e.date}</span>
                <span className="flex items-center gap-1.5"><MapPin size={15} className="text-accent" /> {c.range}</span>
                <span className="flex items-center gap-1.5"><Target size={15} /> {e.distance} • {e.shots} Shots</span>
              </div>
            </div>
            <div className="flex flex-col items-end gap-2.5">
              <Badge tone={statusTone(e.status)}>{e.status}</Badge>
            </div>
          </div>

          <div className="mt-4 space-y-4">
            <div className="min-w-0">
              <ResultsBlock rows={eventResults} />
            </div>

            <div className="space-y-6">
              {/*
                Only what the heading above does not already say. Name, date,
                distance, shot count and status are all up there — printing them
                again a few inches lower adds nothing and costs a screenful.
              */}
              <SectionCard title="Day Information" bodyClassName="px-5 py-3">
                <InfoRow label="Target" value={e.target} />
                <InfoRow label="Scoring" value={e.scoring} />
                <InfoRow label="Max Score" value={formatScore(e.maxScore)} />
                <InfoRow label="Organiser" value={c.organiser} />
                <InfoRow label="Entries Scored" value={eventResults.length} />
              </SectionCard>

              {/*
                The days of this meeting are listed in the navigation to the left,
                where the one you are reading is marked as current. Repeating them
                here gave the same list twice on one screen, the second copy with
                no indication of where you were in it.
              */}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
